import React, { useState, useEffect, useRef, useCallback } from 'react';
import './Tinder.css';
import AuthModal from './AuthModal';
import Notification from './Notification';
import { useNavigate } from 'react-router-dom';

function Tinder() {
  const [images, setImages] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [sessionImages, setSessionImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [, setUser] = useState(null);
  const [notification, setNotification] = useState({ show: false, message: '' });
  const [timeRemaining, setTimeRemaining] = useState(60);
  const [pendingSessionEnd, setPendingSessionEnd] = useState(false);
  const [swipeDirection, setSwipeDirection] = useState(null); // 'left' or 'right'
  const timerRef = useRef(null);
  const navigate = useNavigate();

  // Touch tracking
  const touchStartRef = useRef({ x: 0, y: 0 });

  // Random prompt word
  const PROMPT_WORDS = [
    'drift', 'echo', 'bloom', 'rust', 'hollow', 'tender', 'fracture', 'silk',
    'ember', 'vapor', 'thorn', 'dust', 'ripple', 'ghost', 'pulse', 'shadow',
    'murmur', 'stone', 'glass', 'honey', 'orbit', 'moss', 'static', 'bone',
    'velvet', 'ash', 'tide', 'fog', 'spark', 'loom', 'grain', 'haze',
    'wander', 'fold', 'sway', 'blush', 'glow', 'trace', 'melt', 'nest'
  ];
  const [sessionPrompt] = useState(() => PROMPT_WORDS[Math.floor(Math.random() * PROMPT_WORDS.length)]);
  const [showPrompt, setShowPrompt] = useState(true);

  // Fetch images
  const fetchImages = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/cached-images?count=48');
      if (!response.ok) throw new Error(`Server error: ${response.status}`);
      const data = await response.json();
      setImages(prev => [...prev, ...data]);
      setError(null);
    } catch (err) {
      console.error('Fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchImages();
  }, []);

  // Check auth on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    if (token && storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setIsAuthenticated(true);
        setUser(userData);
        const verifyToken = async () => {
          try {
            const response = await fetch('/api/users/me', {
              headers: { 'x-auth-token': token }
            });
            if (!response.ok) {
              handleLogout();
            } else {
              const freshUserData = await response.json();
              setUser(freshUserData);
            }
          } catch (error) {
            console.error('Error verifying token:', error);
          }
        };
        verifyToken();
      } catch (error) {
        console.error('Error parsing stored user data:', error);
        handleLogout();
      }
    }
  }, []);

  // Flash prompt word then hide
  useEffect(() => {
    const id = setTimeout(() => setShowPrompt(false), 1000);
    return () => clearTimeout(id);
  }, []);

  // Session countdown timer
  useEffect(() => {
    timerRef.current = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  // Save session and navigate to review
  const saveSessionAndNavigate = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      await Promise.all(sessionImages.map(item =>
        fetch('/api/saved-images', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'x-auth-token': token },
          body: JSON.stringify({
            archiveId: item.id,
            title: item.name,
            description: item.description,
            url: item.url,
            thumbnailUrl: item.thumbnailUrl,
            fullSizeUrl: item.fullSizeUrl || null,
          }),
        }).catch(() => {})
      ));
      const response = await fetch('/api/sessions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-auth-token': token },
        body: JSON.stringify({ images: sessionImages, title: sessionPrompt }),
      });
      const data = await response.json();
      if (response.ok) {
        navigate(`/s/${data.code}`);
      } else {
        navigate('/session-review', { state: { images: sessionImages } });
      }
    } catch (error) {
      console.error('Error saving session:', error);
      navigate('/session-review', { state: { images: sessionImages } });
    }
  }, [sessionImages, sessionPrompt, navigate]);

  // End session
  const endSession = useCallback(async () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (sessionImages.length > 0) {
      if (isAuthenticated) {
        await saveSessionAndNavigate();
      } else {
        setPendingSessionEnd(true);
        setShowModal(true);
      }
    } else {
      // Reset timer if no images saved
      setTimeRemaining(60);
      timerRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
  }, [sessionImages, isAuthenticated, saveSessionAndNavigate]);

  // When timer reaches 0, end session
  useEffect(() => {
    if (timeRemaining === 0) {
      endSession();
    }
  }, [timeRemaining, endSession]);

  // Advance to next image, fetch more if needed
  const advanceImage = useCallback(() => {
    setCurrentIndex(prev => {
      const next = prev + 1;
      // Fetch more when running low
      if (next >= images.length - 5) {
        fetchImages();
      }
      return next;
    });
  }, [images.length]);

  // Swipe/choose handler
  const handleChoice = useCallback((direction) => {
    if (currentIndex >= images.length) return;

    setSwipeDirection(direction);

    setTimeout(() => {
      if (direction === 'right') {
        const item = images[currentIndex];
        setSessionImages(prev => {
          const exists = prev.find(img => img.id === item.id);
          if (!exists) return [...prev, item];
          return prev;
        });
        setNotification({ show: true, message: 'Image saved!' });
        setTimeout(() => setNotification({ show: false, message: '' }), 2000);
      }
      setSwipeDirection(null);
      advanceImage();
    }, 300);
  }, [currentIndex, images, advanceImage]);

  // Keyboard controls
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        handleChoice('left');
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        handleChoice('right');
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleChoice]);

  // Touch/swipe gestures
  useEffect(() => {
    const handleTouchStart = (e) => {
      touchStartRef.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      };
    };
    const handleTouchEnd = (e) => {
      const dx = e.changedTouches[0].clientX - touchStartRef.current.x;
      const distance = Math.abs(dx);
      if (distance > 50) {
        handleChoice(dx > 0 ? 'right' : 'left');
      }
    };
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchend', handleTouchEnd, { passive: true });
    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleChoice]);

  const handleAuthModalClose = (success, userData) => {
    setShowModal(false);
    if (success && userData) {
      setIsAuthenticated(true);
      setUser(userData);
      if (pendingSessionEnd) {
        setPendingSessionEnd(false);
        saveSessionAndNavigate();
      }
    } else {
      setPendingSessionEnd(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    setUser(null);
  };

  if (loading && images.length === 0) {
    return <div className="loading">Loading...</div>;
  }

  if (error && images.length === 0) {
    return <div className="error">Error: {error}</div>;
  }

  const currentImage = images[currentIndex];

  return (
    <div className="tinder-container">
      {notification.show && <Notification message={notification.message} />}

      {/* Prompt word flash */}
      {showPrompt && <div className="session-prompt">{sessionPrompt}</div>}

      {/* Main card area */}
      <div className="tinder-stage">
        {currentImage && (
          <div className={`tinder-card ${swipeDirection ? `swipe-${swipeDirection}` : ''}`}>
            <img
              src={currentImage.thumbnailUrl}
              alt={currentImage.name}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "https://archive.org/images/notfound.png";
              }}
            />
          </div>
        )}
        {!currentImage && !loading && (
          <div className="tinder-empty">No more images</div>
        )}
      </div>

      {/* Left/Right click zones */}
      <div className="tinder-zone tinder-zone-left" onClick={() => handleChoice('left')} />
      <div className="tinder-zone tinder-zone-right" onClick={() => handleChoice('right')} />

      {/* Arrow hints */}
      <div className="tinder-hint tinder-hint-left">Skip</div>
      <div className="tinder-hint tinder-hint-right">Save</div>

      {/* Session count */}
      <div className="tinder-count">
        {sessionImages.length} saved
      </div>

      {/* Auth Modal */}
      <AuthModal isOpen={showModal} onClose={handleAuthModalClose} />

      {/* Timer line */}
      <div className="session-timer-line" style={{ width: `${(timeRemaining / 60) * 100}%` }} />

      {/* Done button */}
      <button className="session-done-button" onClick={endSession}>
        Done
      </button>
    </div>
  );
}

export default Tinder;
